import React, { useState, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import AsyncSelect from "react-select/async";
import axios from "../config/axiosInstance";
import { MONITORED_CLIENT } from "../config/apiConfig";
import { useApplication } from "../contexts/ApplicationContext";
import { maskDocument } from "../utils/masks";
import {
  MdRadar,
  MdSearch,
  MdFilterListAlt,
  MdClose,
  MdHistory,
  MdAdd,
  MdEmail,
  MdNotificationsActive,
} from "react-icons/md";
import { LuFilter, LuFilterX } from "react-icons/lu";

const PAGE_SIZE = 20;

function applyDocMask(doc) {
  if (!doc) return "—";
  return maskDocument(doc);
}

function Chip({ label, onClear }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border bg-blue-50 text-blue-700 border-blue-200 whitespace-nowrap">
      {label}
      <button
        onClick={onClear}
        className="hover:opacity-70"
        aria-label="Remover filtro"
      >
        <MdClose size={13} />
      </button>
    </span>
  );
}

const EMPTY_FILTERS = {
  nome: "",
  documento: "",
  tipo: "",
  tipoOcorrencia: "",
  dateFrom: "",
  dateTo: "",
  company: null,
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "38px",
    borderRadius: "12px",
    borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
    backgroundColor: "#f8fafc",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(59,130,246,0.35)" : "none",
    "&:hover": { borderColor: state.isFocused ? "#3b82f6" : "#cbd5e1" },
    fontSize: "14px",
  }),
  placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: "14px" }),
  option: (base, state) => ({
    ...base,
    fontSize: "13px",
    backgroundColor: state.isSelected
      ? "#3b82f6"
      : state.isFocused
        ? "#eff6ff"
        : "white",
    color: state.isSelected ? "white" : "#334155",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "12px",
    overflow: "hidden",
    zIndex: 50,
  }),
  clearIndicator: (base) => ({ ...base, cursor: "pointer", padding: "4px" }),
};

// ── Modals ─────────────────────────────────────────────────────────────────

const HISTORY_TYPES = [
  "Alteração em pendência",
  "Cheque sem fundo",
  "Dados cadastrais",
  "Protesto",
  "Alerta de documento",
  "Consulta realizada",
  "Crédito concedido",
];

function AddHistoryModal({ client, onClose }) {
  const [tipo, setTipo] = useState("");
  const [nota, setNota] = useState("");
  const [sendNotification, setSendNotification] = useState(true);
  const queryClient = useQueryClient();

  const { data: emailsData, isLoading: loadingEmails } = useQuery(
    ["monitored-client-carteira-emails", client.id],
    async () => {
      const { data } = await axios.get(MONITORED_CLIENT.GET_CARTEIRA_EMAILS(client.id));
      return data.data || [];
    },
  );

  const emails = emailsData || [];

  const mutation = useMutation(
    async () => {
      const { data } = await axios.post(
        MONITORED_CLIENT.CREATE_HISTORY(client.id),
        { tipo: tipo || null, nota, sendNotification: sendNotification && emails.length > 0 },
      );
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["monitored-client-history", client.id]);
        onClose();
      },
    },
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl p-6 mx-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-[15px] font-bold text-slate-800">
              Adicionar ao histórico
            </h2>
            <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[280px]">
              {client.nome || applyDocMask(client.documento)}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition">
            <MdClose size={20} />
          </button>
        </div>

        <div className="mb-2.5">
          <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Tipo de ocorrência</label>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
          >
            <option value="">Selecione o tipo...</option>
            {HISTORY_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <textarea
          rows={4}
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          placeholder="Descreva a ocorrência, alteração ou anotação..."
          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
        />

        {/* Notification section */}
        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2.5">
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={sendNotification}
              onChange={(e) => setSendNotification(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            />
            <div className="flex items-center gap-1.5">
              <MdNotificationsActive size={14} className={sendNotification ? "text-blue-600" : "text-slate-400"} />
              <span className="text-sm font-medium text-slate-700">Enviar notificação por e-mail</span>
            </div>
          </label>

          {sendNotification && (
            <div className="pl-6">
              {loadingEmails ? (
                <p className="text-xs text-slate-400">Carregando e-mails...</p>
              ) : emails.length === 0 ? (
                <p className="text-xs text-amber-600">Nenhum e-mail cadastrado na carteira desta empresa.</p>
              ) : (
                <div className="space-y-1">
                  {emails.map((email) => (
                    <div key={email} className="flex items-center gap-1.5">
                      <MdEmail size={11} className="text-slate-400 flex-shrink-0" />
                      <span className="text-xs text-slate-500">{email}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {mutation.isError && (
          <p className="mt-2 text-xs text-red-600">
            {mutation.error?.response?.data?.message || "Erro ao salvar."}
          </p>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
          >
            Cancelar
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!nota.trim() || mutation.isLoading}
            className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-4 py-2 text-sm font-semibold text-white transition"
          >
            {mutation.isLoading ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function HistoryModal({ client, onClose, onAdd }) {
  const { data, isLoading } = useQuery(
    ["monitored-client-history", client.id],
    async () => {
      const { data } = await axios.get(MONITORED_CLIENT.GET_HISTORY(client.id));
      return data.data;
    },
  );

  const entries = data || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl mx-4 flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between p-3 border-b border-slate-100">
          <div>
            <h2 className="text-[15px] font-bold text-slate-800">Histórico</h2>
            <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[300px]">
              {client.nome || applyDocMask(client.documento)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onAdd}
              className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 px-3 py-1.5 text-xs font-semibold text-white transition"
            >
              <MdAdd size={14} />
              Adicionar
            </button>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition"
            >
              <MdClose size={20} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-5 py-4 space-y-3">
          {isLoading ? (
            <div className="flex justify-center text-slate-400 text-sm">
              Carregando...
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center py-10 gap-2 text-slate-400">
              <MdHistory size={32} className="opacity-25" />
              <p className="text-sm">Nenhuma entrada no histórico.</p>
            </div>
          ) : (
            entries.map((e) => (
              <div
                key={e.id}
                className="rounded-xl border border-slate-100 bg-slate-50 p-2"
              >
                <p className="text-sm text-slate-700 whitespace-pre-wrap">
                  {e.nota}
                </p>
                <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-slate-100">
                  <span className="text-xs text-slate-400">
                    {new Date(e.createdAt).toLocaleString("pt-BR")}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

const AdminMonitoredClientsPage = () => {
  const { user, userPermission } = useApplication();
  const isPowerUser = user?.companyId == 1 && userPermission === "POWER_USER";

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const debounceRef = useRef(null);

  // history modal state
  const [historyTarget, setHistoryTarget] = useState(null); // client object
  const [addTarget, setAddTarget] = useState(null); // client object

  const { data, isLoading } = useQuery(
    ["admin-monitored-clients", applied, page],
    async () => {
      const params = { page, pageSize: PAGE_SIZE };
      if (applied.nome) params.nome = applied.nome;
      if (applied.documento) params.documento = applied.documento;
      if (applied.tipo) params.tipo = applied.tipo;
      if (applied.tipoOcorrencia) params.tipoOcorrencia = applied.tipoOcorrencia;
      if (applied.dateFrom) params.dateFrom = applied.dateFrom;
      if (applied.dateTo) params.dateTo = applied.dateTo;
      if (applied.company?.value)
        params.filterCompanyId = applied.company.value;
      const { data } = await axios.get(MONITORED_CLIENT.ADMIN_LIST, { params });
      return data;
    },
    { keepPreviousData: true },
  );

  const loadCompanies = useCallback((inputValue, callback) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(MONITORED_CLIENT.COMPANIES_SEARCH, {
          params: { search: inputValue },
        });
        callback(data.data || []);
      } catch {
        callback([]);
      }
    }, 300);
  }, []);

  const rows = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const companyActive = applied.company?.value
    ? [["company", applied.company]]
    : [];
  const allActiveFilters = [
    ...Object.entries(applied).filter(([k, v]) => k !== "company" && v),
    ...companyActive,
  ];

  const handleApply = () => {
    setApplied({ ...filters });
    setPage(1);
  };
  const handleClear = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(1);
  };
  const handleRemoveChip = (key) => {
    const next = { ...applied, [key]: key === "company" ? null : "" };
    setApplied(next);
    setFilters(next);
    setPage(1);
  };

  const filterLabel = (key, value) => {
    const labels = {
      nome: "Nome",
      documento: "Documento",
      tipo: "Tipo",
      tipoOcorrencia: "Ocorrência",
      dateFrom: "De",
      dateTo: "Até",
      company: "Empresa",
    };
    return `${labels[key]}: ${key === "company" ? value?.label : value}`;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center shadow-sm shadow-blue-200">
            <MdRadar size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-[15px] font-bold text-slate-800 leading-none">
              Gerenciamento de Carteira
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">
              {total} registro{total !== 1 ? "s" : ""} encontrado
              {total !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
        <button
          onClick={() => setFiltersOpen((v) => !v)}
          className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-medium transition-all ${filtersOpen ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
        >
          {filtersOpen ? <LuFilterX size={15} /> : <LuFilter size={15} />}
          Filtros
          {allActiveFilters.length > 0 && (
            <span className="ml-0.5 h-4 min-w-[18px] px-1 flex items-center justify-center rounded-full bg-blue-600 text-white text-[10px] font-bold">
              {allActiveFilters.length}
            </span>
          )}
        </button>
      </div>

      {/* Filters panel */}
      {filtersOpen && (
        <div className="rounded-2xl border border-slate-200/70 bg-white shadow-sm p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                Nome / Razão Social
              </label>
              <div className="relative">
                <MdSearch
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
                <input
                  type="text"
                  value={filters.nome}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, nome: e.target.value }))
                  }
                  onKeyDown={(e) => e.key === "Enter" && handleApply()}
                  placeholder="Buscar por nome..."
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 pl-8 pr-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                Documento (CPF/CNPJ)
              </label>
              <div className="relative">
                <MdSearch
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
                <input
                  type="text"
                  value={filters.documento}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, documento: e.target.value }))
                  }
                  onKeyDown={(e) => e.key === "Enter" && handleApply()}
                  placeholder="000.000.000-00"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 pl-8 pr-3 py-2 text-sm font-mono text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                Tipo
              </label>
              <select
                value={filters.tipo}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, tipo: e.target.value }))
                }
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              >
                <option value="">Todos</option>
                <option value="CPF">CPF</option>
                <option value="CNPJ">CNPJ</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                Tipo de ocorrência
              </label>
              <select
                value={filters.tipoOcorrencia}
                onChange={(e) => setFilters((f) => ({ ...f, tipoOcorrencia: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              >
                <option value="">Todos</option>
                {HISTORY_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                Empresa (quem adicionou)
              </label>
              <AsyncSelect
                isClearable
                placeholder="Buscar empresa..."
                loadOptions={loadCompanies}
                defaultOptions
                value={filters.company}
                onChange={(opt) =>
                  setFilters((f) => ({ ...f, company: opt || null }))
                }
                styles={selectStyles}
                loadingMessage={() => "Buscando..."}
                noOptionsMessage={({ inputValue }) =>
                  inputValue
                    ? "Nenhuma empresa encontrada"
                    : "Digite para buscar"
                }
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                Data de adição — De
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, dateFrom: e.target.value }))
                }
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                Data de adição — Até
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, dateTo: e.target.value }))
                }
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
            <button
              onClick={handleClear}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
            >
              <LuFilterX size={14} />
              Limpar
            </button>
            <button
              onClick={handleApply}
              className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition"
            >
              <MdFilterListAlt size={15} />
              Aplicar filtros
            </button>
          </div>
        </div>
      )}

      {/* Active filter chips */}
      {allActiveFilters.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {allActiveFilters.map(([key, value]) => (
            <Chip
              key={key}
              label={filterLabel(key, value)}
              onClear={() => handleRemoveChip(key)}
            />
          ))}
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-10 text-slate-400 text-sm gap-2">
            <svg
              className="animate-spin h-4 w-4 text-blue-500"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8z"
              />
            </svg>
            Carregando...
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2 text-slate-400">
            <MdRadar size={36} className="opacity-25" />
            <p className="text-sm">Nenhum registro encontrado.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide border-b border-slate-100">
                <tr>
                  <th className="px-5 py-3 font-semibold">Empresa (cliente)</th>
                  <th className="px-5 py-3 font-semibold">
                    Nome / Razão Social
                  </th>
                  <th className="px-5 py-3 font-semibold">Documento</th>
                  <th className="px-5 py-3 font-semibold">Tipo</th>
                  <th className="px-5 py-3 font-semibold">Adicionado em</th>
                  {isPowerUser && (
                    <th className="px-5 py-3 font-semibold">Ações</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="hover:bg-slate-50/60 transition-colors"
                  >
                    <td className="px-5 py-3.5">
                      <span className="text-[13px] font-semibold text-slate-700">
                        {r.Company?.name || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-slate-600">
                      {r.nome || "—"}
                    </td>
                    <td className="px-5 py-3.5 font-mono text-slate-600">
                      {applyDocMask(r.documento)}
                    </td>
                    <td className="px-5 py-3.5">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${r.tipo === "CPF" ? "bg-blue-50 text-blue-700" : "bg-violet-50 text-violet-700"}`}
                      >
                        {r.tipo}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-slate-400 text-xs">
                      {new Date(r.createdAt).toLocaleDateString("pt-BR")}
                    </td>
                    {isPowerUser && (
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => setAddTarget(r)}
                            title="Adicionar ao histórico"
                            className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 transition"
                          >
                            <MdAdd size={13} />
                            Adicionar
                          </button>
                          <button
                            onClick={() => setHistoryTarget(r)}
                            title="Ver histórico"
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 transition"
                          >
                            <MdHistory size={13} />
                            Histórico
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50/50">
            <p className="text-xs text-slate-400">
              Página {page} de {totalPages} · {total} registros
            </p>
            <div className="flex gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {addTarget && (
        <AddHistoryModal
          client={addTarget}
          onClose={() => setAddTarget(null)}
        />
      )}
      {historyTarget && (
        <HistoryModal
          client={historyTarget}
          onClose={() => setHistoryTarget(null)}
          onAdd={() => {
            setAddTarget(historyTarget);
            setHistoryTarget(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminMonitoredClientsPage;
